import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { StepIndicator } from "./components/StepIndicator";
import { UploadStep } from "./components/UploadStep";
import { ProcessingStep } from "./components/ProcessingStep";
import { ReviewStep } from "./components/ReviewStep";
import { GeneratingStep } from "./components/GeneratingStep";
import { DownloadStep } from "./components/DownloadStep";
import { PdfRegionSelectorDialog } from "./components/PdfRegionSelectorDialog";
import { DebugPanel } from "./components/DebugPanel";
import { useWorkflow, combineParsed, type ProgressMessage } from "./state";
import { createDocument, updateDocument } from "@/features/history/api";
import {
  extractFile,
  extractFileStreaming,
  parseExtractions,
  parseExtractionsStreaming,
  generatePdf,
  exportExcel,
  savePropertyInfo,
  startSession,
  endSession,
  recordObjection,
  recordCorrection,
  type CorrectionType,
} from "./api";
import { usePropertyInfo } from "./property/usePropertyInfo";
import { toast } from "@/lib/toast";
import { triggerBlobDownload } from "@/lib/download";
import {
  detectFileKind,
  fileTypeLabel,
  type FileUploadItem,
  type ParsedExtraction,
} from "./types";

interface ConfigDto {
  googleMapsApiKey: string | null;
}

export function UploadPage() {
  const { state, dispatch, reset } = useWorkflow();
  const configQuery = useQuery<ConfigDto>({
    queryKey: ["config"],
    queryFn: () => api<ConfigDto>("/api/config"),
    staleTime: Infinity,
  });
  // AbortController for the in-flight extract/parse/generate streams. The
  // Cancel buttons in Processing / Generating call .abort() on this; the
  // streaming fetches receive the signal and bail cleanly.
  const abortRef = useRef<AbortController | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string | null>(null);
  const [regionDialogFileId, setRegionDialogFileId] = useState<string | null>(null);
  const [addMoreInFlight, setAddMoreInFlight] = useState(false);
  const regionDialogFile = state.files.find((f) => f.id === regionDialogFileId);

  // Address verification + saved-property resolution — shared with the PAA flow.
  // Driven by a merge-patch dispatch so it works with this page's reducer.
  const propertyInfo = usePropertyInfo({
    active: state.step === "Review",
    data: state.data,
    patch: (p) => dispatch({ type: "UPDATE_DATA", patch: p }),
    googleMapsApiKey: configQuery.data?.googleMapsApiKey,
  });

  // Snapshot for correction-stat diffs (initialized on COMBINED). We use a
  // ref because the comparison reads from "original" but never mutates it.
  const originalRef = useRef<ParsedExtraction | null>(null);
  useEffect(() => {
    originalRef.current = state.originalData;
  }, [state.originalData]);

  // Track objection-id -> server-side stats-DB id, so corrections can be
  // tagged to the right row. Mirrors Blazor's _objectionIdMapping.
  const objectionStatIdRef = useRef<Record<string, string>>({});

  // Keep the archived draft in sync with manual edits in Review. Debounced;
  // the ref skips the redundant write right after create (and after each
  // successful sync) so we only PUT when the data object actually changed.
  const lastSyncedDataRef = useRef<ParsedExtraction | null>(null);
  useEffect(() => {
    if (state.step !== "Review" || !state.documentId || !state.data) return;
    if (lastSyncedDataRef.current === state.data) return;
    const docId = state.documentId;
    const data = state.data;
    const t = setTimeout(() => {
      lastSyncedDataRef.current = data;
      updateDocument("Objections", docId, data).catch(() => {
        // Retry on the next edit — the generate call persists the final
        // state regardless.
        lastSyncedDataRef.current = null;
      });
    }, 1200);
    return () => clearTimeout(t);
  }, [state.data, state.step, state.documentId]);

  // ---------------------------------------------------------------------------
  // Correction tracking — fires on blur, only records if value differs.
  // ---------------------------------------------------------------------------
  const fieldTypeMap: Partial<Record<keyof ParsedExtraction, CorrectionType>> = {
    address: "Address",
    jobNumber: "JobNumber",
  };

  const handleFieldBlur = useCallback(
    async (field: keyof ParsedExtraction) => {
      const sid = state.sessionId;
      if (!sid || !state.data) return;
      const original = (originalRef.current?.[field] ?? "") as string;
      const current = (state.data[field] ?? "") as string;
      if (String(original) === String(current)) return;
      try {
        await recordCorrection(sid, {
          fieldName: String(field),
          originalValue: String(original ?? ""),
          correctedValue: String(current ?? ""),
          type: fieldTypeMap[field] ?? "Other",
        });
      } catch {
        /* swallow — non-fatal */
      }
    },
    [state.sessionId, state.data, fieldTypeMap],
  );

  const handleObjectionBlur = useCallback(
    async (id: string) => {
      const sid = state.sessionId;
      if (!sid || !state.data) return;
      const current = state.data.objections.find((o) => o.id === id);
      const original = originalRef.current?.objections.find((o) => o.id === id);
      if (!current) return;
      if (current.objectionText === (original?.objectionText ?? "")) return;
      try {
        await recordCorrection(sid, {
          objectionId: objectionStatIdRef.current[id] ?? null,
          fieldName: "ObjectionText",
          originalValue: original?.objectionText ?? "[Not Present]",
          correctedValue: current.objectionText,
          type: "ObjectionText",
        });
      } catch {
        /* swallow */
      }
    },
    [state.sessionId, state.data],
  );

  // ---------------------------------------------------------------------------
  // File intake
  // ---------------------------------------------------------------------------
  const handleAddFiles = (files: File[]) => {
    const items: FileUploadItem[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      status: "Pending",
      kind: detectFileKind(file.name),
    }));
    dispatch({ type: "ADD_FILES", files: items });
  };

  // ---------------------------------------------------------------------------
  // Process flow — start session, extract each file, parse, combine, record
  // stats, transition to Review.
  // ---------------------------------------------------------------------------
  // Pushes a single progress event with timestamp + optional [file] prefix.
  const pushProgress = useCallback(
    (message: string, fileName?: string) => {
      const ts = new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      dispatch({
        type: "APPEND_PROGRESS",
        message: { timestamp: ts, fileName, message } as ProgressMessage,
      });
    },
    [dispatch],
  );

  // Helper: was this error from a user-initiated abort?
  const isAbort = (err: unknown) =>
    (err as { name?: string })?.name === "AbortError";

  const handleCancelProcess = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const handleCancelGenerate = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const handleProcess = useCallback(async () => {
    const pending = state.files.filter((f) => f.status !== "Completed");
    if (pending.length === 0) return;

    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    dispatch({ type: "CLEAR_PROGRESS" });
    dispatch({ type: "START_PROCESSING" });
    pushProgress(`Starting to process ${pending.length} file(s)...`);

    // Start stats session. Type per clarification #3 (fix): per-file type,
    // or "Batch" when 2+ files.
    let sessionId: string | null = null;
    try {
      const sessionType =
        pending.length === 1 ? fileTypeLabel(pending[0].file.name) : "Batch";
      const sessionName =
        pending.length === 1 ? pending[0].file.name : `${pending.length} files`;
      sessionId = await startSession(sessionType, sessionName);
      dispatch({ type: "SET_SESSION_ID", sessionId });
    } catch {
      // Non-fatal; processing continues without stats.
    }

    // EXTRACT — serial; uses the streaming endpoint so per-page progress
    // messages from FileExtractionService flow into the activity log.
    const extractedById: Record<string, FileUploadItem["extraction"]> = {};
    for (const f of pending) {
      setCurrentFileName(f.file.name);
      pushProgress("Extracting text and annotations...", f.file.name);
      try {
        const regionsJson = f.tableRegions
          ? JSON.stringify(f.tableRegions)
          : undefined;
        const extraction = await extractFileStreaming(f.file, {
          tableRegionsJson: regionsJson,
          onProgress: (msg) => pushProgress(msg, f.file.name),
          signal,
        });
        extractedById[f.id] = extraction;
        dispatch({ type: "FILE_PROCESSED", id: f.id, extraction });
      } catch (err) {
        if (isAbort(err) || signal.aborted) {
          // User cancelled — bail silently, reset state.
          if (sessionId) {
            await endSession(sessionId, {
              totalObjections: 0,
              status: "Cancelled",
              errorMessage: "Cancelled",
            }).catch(() => {});
          }
          toast.info("Processing cancelled");
          dispatch({ type: "SET_STEP", step: "Upload" });
          abortRef.current = null;
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        pushProgress(`Extraction failed: ${msg}`, f.file.name);
        dispatch({ type: "FILE_FAILED", id: f.id, error: msg });
      }
    }
    setCurrentFileName(null);

    const succeeded = Object.entries(extractedById);
    if (succeeded.length === 0) {
      toast.error(
        "All files failed to process. Please check the errors and try again.",
      );
      if (sessionId) {
        await endSession(sessionId, {
          totalObjections: 0,
          status: "Error",
          errorMessage: "All files failed",
        }).catch(() => {});
      }
      dispatch({ type: "SET_STEP", step: "Upload" });
      return;
    }

    // PARSE — streaming variant so AI parser chatter shows up live.
    try {
      pushProgress(
        succeeded.length === 1
          ? "Analyzing with AI..."
          : `Analyzing all files with AI (single batch call)...`,
      );
      const parsedList = await parseExtractionsStreaming(
        succeeded.map(([, ext]) => ext!),
        {
          sessionId: sessionId ?? undefined,
          onProgress: (msg) => pushProgress(msg),
          signal,
        },
      );
      const parsedByFileId: Record<string, ParsedExtraction> = {};
      succeeded.forEach(([id], idx) => {
        parsedByFileId[id] = parsedList[idx];
      });
      dispatch({ type: "PARSED", parsedByFileId });

      const updatedFiles = state.files.map((f) =>
        parsedByFileId[f.id]
          ? {
              ...f,
              parsed: parsedByFileId[f.id],
              status: "Completed" as const,
              extraction: extractedById[f.id],
            }
          : f.id in extractedById
            ? {
                ...f,
                status: "Completed" as const,
                extraction: extractedById[f.id],
              }
            : f,
      );
      const combined = combineParsed(updatedFiles);
      if (!combined) {
        toast.error("Parse returned no results.");
        if (sessionId) {
          await endSession(sessionId, {
            totalObjections: 0,
            status: "Error",
            errorMessage: "Parse returned no results",
          }).catch(() => {});
        }
        dispatch({ type: "SET_STEP", step: "Upload" });
        return;
      }
      dispatch({ type: "COMBINED", data: combined });
      propertyInfo.reset(); // fresh extraction — re-verify the new address

      // Persist the run immediately — the draft lands in History as soon as
      // extraction finishes, before any manual review. Best-effort: the flow
      // works the same if the save fails.
      try {
        const docId = await createDocument("Objections", combined, sessionId);
        lastSyncedDataRef.current = combined;
        dispatch({ type: "SET_DOCUMENT_ID", documentId: docId });
      } catch {
        /* non-fatal */
      }

      // Record one stats row per parsed objection, mapping back to DB id.
      if (sessionId) {
        const map: Record<string, string> = {};
        for (const o of combined.objections) {
          try {
            const id = await recordObjection(sessionId, {
              address: combined.address ?? "",
              jobNumber: combined.jobNumber ?? "",
              objectionText: o.objectionText,
            });
            map[o.id] = id;
          } catch {
            /* swallow per-objection */
          }
        }
        objectionStatIdRef.current = map;
      }

      toast.success(
        `Completed: found ${combined.objections.length} objection(s)`,
      );
      abortRef.current = null;
    } catch (err) {
      if (isAbort(err) || signal.aborted) {
        if (sessionId) {
          await endSession(sessionId, {
            totalObjections: 0,
            status: "Cancelled",
            errorMessage: "Cancelled",
          }).catch(() => {});
        }
        toast.info("Processing cancelled");
        dispatch({ type: "SET_STEP", step: "Upload" });
        abortRef.current = null;
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`AI parse failed: ${msg}`);
      if (sessionId) {
        await endSession(sessionId, {
          totalObjections: 0,
          status: "Error",
          errorMessage: msg,
        }).catch(() => {});
      }
      dispatch({ type: "SET_STEP", step: "Upload" });
      abortRef.current = null;
    }
  }, [state.files, dispatch]);

  // ---------------------------------------------------------------------------
  // Add More Files — process and merge into existing data without touching
  // property fields. Mirrors Blazor's "Add More Files" + MergeNewData.
  // ---------------------------------------------------------------------------
  const handleAddMoreFiles = useCallback(
    async (files: File[]) => {
      if (!state.data) return;
      const existingKeys = new Set(
        state.files.map((f) => `${f.file.name}|${f.file.size}`),
      );
      const newItems: FileUploadItem[] = [];
      for (const f of files) {
        if (existingKeys.has(`${f.name}|${f.size}`)) {
          toast.warning(`File '${f.name}' already uploaded`);
          continue;
        }
        newItems.push({
          id: crypto.randomUUID(),
          file: f,
          status: "Processing",
          kind: detectFileKind(f.name),
        });
      }
      if (newItems.length === 0) return;
      dispatch({ type: "ADD_FILES", files: newItems });
      setAddMoreInFlight(true);

      let totalAdded = 0;
      let totalFailed = 0;

      try {
        for (const item of newItems) {
          try {
            const extraction = await extractFile(item.file);
            dispatch({
              type: "FILE_PROCESSED",
              id: item.id,
              extraction,
            });
            const parsedList = await parseExtractions(
              [extraction],
              state.sessionId ?? undefined,
            );
            const parsed = parsedList[0];
            const stamped = parsed.objections.map((o) => ({
              ...o,
              sourceFileName: o.sourceFileName ?? item.file.name,
            }));
            dispatch({
              type: "MERGE_NEW_OBJECTIONS",
              objections: stamped,
            });
            // Record stats per new objection
            if (state.sessionId) {
              for (const o of stamped) {
                try {
                  const sid = await recordObjection(state.sessionId, {
                    address: state.data!.address ?? "",
                    jobNumber: state.data!.jobNumber ?? "",
                    objectionText: o.objectionText,
                  });
                  objectionStatIdRef.current[o.id] = sid;
                } catch {
                  /* swallow */
                }
              }
            }
            totalAdded += stamped.length;
            toast.success(
              `Added ${stamped.length} objections from ${item.file.name}`,
            );
          } catch (err) {
            totalFailed++;
            const msg = err instanceof Error ? err.message : String(err);
            dispatch({ type: "FILE_FAILED", id: item.id, error: msg });
          }
        }
        if (totalAdded > 0) {
          toast.success(
            `Successfully processed ${newItems.length - totalFailed} file(s), added ${totalAdded} objections`,
          );
        }
        if (totalFailed > 0) {
          toast.error(`${totalFailed} file(s) failed to process`);
        }
      } finally {
        setAddMoreInFlight(false);
      }
    },
    [state.data, state.files, state.sessionId, dispatch],
  );

  // ---------------------------------------------------------------------------
  // Add/remove objection — wraps reducer + stats recording.
  // ---------------------------------------------------------------------------
  const handleAddObjection = useCallback(async () => {
    dispatch({ type: "ADD_OBJECTION" });
    if (state.sessionId) {
      try {
        await recordCorrection(state.sessionId, {
          fieldName: "Objection",
          originalValue: "[Not Present]",
          correctedValue: "[Manually Added]",
          type: "Other",
        });
      } catch {
        /* swallow */
      }
    }
  }, [dispatch, state.sessionId]);

  const handleRemoveObjection = useCallback(
    async (id: string) => {
      const removed = state.data?.objections.find((o) => o.id === id);
      dispatch({ type: "REMOVE_OBJECTION", id });
      if (state.sessionId && removed) {
        try {
          await recordCorrection(state.sessionId, {
            objectionId: objectionStatIdRef.current[id] ?? null,
            fieldName: "Objection",
            originalValue: removed.objectionText,
            correctedValue: "[Removed]",
            type: "Other",
          });
        } catch {
          /* swallow */
        }
      }
    },
    [dispatch, state.sessionId, state.data],
  );

  // ---------------------------------------------------------------------------
  // Excel export
  // ---------------------------------------------------------------------------
  const handleExportExcel = useCallback(async () => {
    if (!state.data) {
      toast.warning("No data to export");
      return;
    }
    try {
      const { blob, filename } = await exportExcel(
        state.data,
        undefined,
        state.sessionId,
        state.documentId,
      );
      triggerBlobDownload(blob, filename);
      toast.success(
        `Exported ${state.data.objections.length} objections to Excel`,
      );

      // Excel is a real deliverable — record it as a session outcome. Sends
      // "Exported"; the server only upgrades status, so if the PDF was already
      // generated this leaves it Completed and just adds the Excel output.
      if (state.sessionId) {
        await endSession(state.sessionId, {
          totalObjections: state.data.objections.length,
          resolvedObjections: state.data.objections.filter((o) => o.resolved)
            .length,
          status: "Exported",
          producedExcel: true,
        }).catch(() => {});
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to export: ${msg}`);
    }
  }, [state.data, state.sessionId, state.documentId]);

  // ---------------------------------------------------------------------------
  // Generate flow — save property info first, generate, end session.
  // ---------------------------------------------------------------------------
  const handleGenerate = useCallback(async () => {
    if (!state.data) return;
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;
    dispatch({ type: "GENERATING" });
    try {
      // Save property info before generation (mirrors Blazor)
      const firstFile = state.files.find((f) => f.status === "Completed");
      if (state.data.address) {
        try {
          await savePropertyInfo({
            address: state.data.address,
            cbNumber: state.data.cbNumber,
            bin: state.data.bin,
            applicantName: state.data.applicantName,
            borough: state.data.borough,
            block: state.data.block,
            lot: state.data.lot,
            sourceFileName: firstFile?.file.name ?? null,
          });
        } catch {
          /* swallow — non-fatal */
        }
      }

      const { blob, filename } = await generatePdf(
        state.data,
        signal,
        state.sessionId,
        state.documentId,
      );
      const url = URL.createObjectURL(blob);
      dispatch({ type: "GENERATED", url, filename });

      // End session on success — PDF produced. Capture the resolved count
      // (resolved objections are excluded from the deliverable).
      if (state.sessionId) {
        await endSession(state.sessionId, {
          totalObjections: state.data.objections.length,
          resolvedObjections: state.data.objections.filter((o) => o.resolved)
            .length,
          status: "Completed",
          producedPdf: true,
        }).catch(() => {});
      }
      abortRef.current = null;
    } catch (err) {
      if (isAbort(err) || signal.aborted) {
        toast.info("Generation cancelled");
        dispatch({ type: "GENERATE_FAILED", error: "Cancelled" });
        abortRef.current = null;
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to generate PDF: ${msg}`);
      dispatch({ type: "GENERATE_FAILED", error: msg });
      if (state.sessionId) {
        await endSession(state.sessionId, {
          totalObjections: state.data?.objections.length ?? 0,
          status: "Error",
          errorMessage: msg,
        }).catch(() => {});
      }
      abortRef.current = null;
    }
  }, [state.data, state.files, state.sessionId, state.documentId, dispatch]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="max-w-[1100px]">
      <div className="mb-10">
        <StepIndicator current={state.step} completed={state.completedSteps} />
      </div>

      {state.step === "Upload" && (
        <UploadStep
          files={state.files}
          onAddFiles={handleAddFiles}
          onRemoveFile={(id) => dispatch({ type: "REMOVE_FILE", id })}
          onOpenRegionSelector={(id) => setRegionDialogFileId(id)}
          onSetFileKind={(id, kind) =>
            dispatch({ type: "SET_FILE_KIND", id, kind })
          }
          onProcess={handleProcess}
          onContinueToReview={() =>
            dispatch({ type: "SET_STEP", step: "Review" })
          }
          processing={false}
          hasReviewData={state.data !== null}
        />
      )}

      {regionDialogFile && (
        <PdfRegionSelectorDialog
          open={!!regionDialogFile}
          file={regionDialogFile.file}
          existing={regionDialogFile.tableRegions}
          onClose={() => setRegionDialogFileId(null)}
          onSave={(regions) => {
            dispatch({
              type: "SET_FILE_REGIONS",
              id: regionDialogFile.id,
              regions,
            });
            const pageCount = Object.keys(regions.regions).length;
            toast.success(
              `Table regions saved for ${pageCount} page(s) in ${regionDialogFile.file.name}`,
            );
            setRegionDialogFileId(null);
          }}
        />
      )}

      {state.step === "Processing" && (
        <ProcessingStep
          files={state.files}
          currentFileName={currentFileName}
          progressMessages={state.progressMessages}
          onCancel={handleCancelProcess}
        />
      )}

      {state.step === "Review" && state.data && (
        <ReviewStep
          data={state.data}
          files={state.files}
          onPatch={(patch) => dispatch({ type: "UPDATE_DATA", patch })}
          onPatchObjection={(id, patch) =>
            dispatch({ type: "UPDATE_OBJECTION", id, patch })
          }
          onAssignAll={(assignedTo) =>
            dispatch({ type: "ASSIGN_ALL_OBJECTIONS", assignedTo })
          }
          onAddObjection={handleAddObjection}
          onRemoveObjection={handleRemoveObjection}
          onBack={() => dispatch({ type: "SET_STEP", step: "Upload" })}
          onGenerate={handleGenerate}
          onExportExcel={handleExportExcel}
          onAddMoreFiles={handleAddMoreFiles}
          addMoreInFlight={addMoreInFlight}
          savedPropertyInfo={propertyInfo.savedInfo}
          savedPropertyInfoSearching={propertyInfo.savedInfoSearching}
          onApplySavedPropertyInfo={
            propertyInfo.savedInfo ? propertyInfo.applySavedInfo : undefined
          }
          onFieldBlur={handleFieldBlur}
          onObjectionTextBlur={handleObjectionBlur}
          googleMapsApiKey={configQuery.data?.googleMapsApiKey}
          addressVerifying={propertyInfo.addressVerifying}
          debugPanel={
            state.data.debugInfo ? (
              <DebugPanel info={state.data.debugInfo} />
            ) : null
          }
        />
      )}

      {state.step === "Generating" && state.data && (
        <GeneratingStep
          activeCount={state.data.objections.filter((o) => !o.resolved).length}
          onCancel={handleCancelGenerate}
        />
      )}

      {state.step === "Download" &&
        state.data &&
        state.pdfUrl &&
        state.pdfFilename && (
          <DownloadStep
            data={state.data}
            pdfUrl={state.pdfUrl}
            filename={state.pdfFilename}
            onStartOver={reset}
            onExportExcel={handleExportExcel}
            onBackToReview={() => dispatch({ type: "BACK_FROM_DOWNLOAD" })}
          />
        )}
    </div>
  );
}
